import {
  Divider as FlicketDivider,
  DividerProps,
  LinkText,
  Stack,
  SystemProps,
  Text,
  TextProps,
  VStack,
} from "flicket-ui";
import {
  OrderSummaryProvider,
  useOrderSummaryContext,
} from "./context/OrderSummaryContext";
import { ListItem, ListItemProps } from "./components/ListItem";
import { AnimateReveal } from "~components/AnimateReveal/AnimateReveal";
import { ReactNode, useState } from "react";
import { FixedRatioImage } from "~components/common/Image";

function LineItems() {
  const { lineItems } = useOrderSummaryContext();

  return (
    <>
      {lineItems?.map((item) => {
        return (
          <ListItem
            key={item.id}
            title={item.title}
            subtitle={item.subtitle}
            price={item.price * item.quantity}
          />
        );
      })}
    </>
  );
}

function ListItemInternal(props: ListItemProps & Omit<SystemProps, "title">) {
  return <ListItem {...props} />;
}

function ServiceFeeItems() {
  const { serviceCosts, isLoading } = useOrderSummaryContext();
  return (
    <>
      {serviceCosts.map(({ price, title, subtitle }) => (
        <ListItem
          key={title}
          title={title}
          subtitle={subtitle}
          price={price}
          isLoading={isLoading}
        />
      ))}
    </>
  );
}

export const ServiceFeesToggle = ({ children }: { children: ReactNode }) => {
  const { isLoading, totalServiceCost } = useOrderSummaryContext();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Stack direction={"vertical"}>
      <ListItem
        title={<LinkText>Service costs</LinkText>}
        onClick={() => setShowDetails(!showDetails)}
        price={!showDetails ? totalServiceCost : undefined}
        isLoading={isLoading}
      />
      <AnimateReveal show={showDetails}>
        <Stack direction={"vertical"} gap={1} mt={1}>
          {children}
        </Stack>
      </AnimateReveal>
    </Stack>
  );
};

function Total(props: Partial<ListItemProps & SystemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();
  return (
    <ListItem
      title={"Total"}
      price={totals?.remainingPayment}
      variant="header.S"
      isLoading={isLoading}
      loaderWidth={80}
      {...props}
    />
  );
}

function OrderSummaryText(props: Partial<ListItemProps>) {
  return <ListItem title={"Order summary"} {...props} />;
}

function Divider(props: DividerProps) {
  return <FlicketDivider {...props} />;
}

function PromoCodeInput() {
  return <PromoCodeInput />;
}

function PromoCodeSummary() {
  return <PromoCodeSummary />;
}

function TicketProtectionFee(props: Partial<ListItemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();

  return (
    <AnimateReveal initial={true} show={!!totals?.ticketCover}>
      <ListItem
        title={"Ticket protection cover"}
        price={totals?.ticketCover}
        isLoading={isLoading}
        {...props}
      />
    </AnimateReveal>
  );
}

function DueNow(props: Partial<ListItemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();
  return (
    <ListItem
      title="Due now"
      variant="header.S"
      price={totals?.remainingPayment}
      isLoading={isLoading}
      loaderWidth={80}
      {...props}
    />
  );
}

/**
 * To be completed
 */
function DueLater() {
  return null;
}

/**
 * To be completed
 */
function EventImage() {
  const { image, title } = useOrderSummaryContext();

  return (
    <FixedRatioImage
      alt={title}
      aspectRatio="5 / 2"
      image={image}
      width={"100%"}
      overflow="hidden"
      borderRadius={"md"}
      useNearFitCover={true}
    />
  );
}

function EventInfo({
  variant = "regular",
}: {
  variant?: TextProps["variant"];
}) {
  const { subtitle, title } = useOrderSummaryContext();
  return (
    <VStack gap={"1/2"}>
      <Text variant={variant}>{title}</Text>
      <Text variant="small">{subtitle}</Text>
    </VStack>
  );
}

function AllLineItemsAndFees() {
  return (
    <>
      <OrderSummary.LineItems />
      <OrderSummary.TicketProtectionFee />

      <OrderSummary.ServiceFeesToggle>
        <OrderSummary.ServiceFeeItems />
      </OrderSummary.ServiceFeesToggle>
    </>
  );
}

export const OrderSummary = {
  Provider: OrderSummaryProvider,

  ListItem: ListItemInternal,
  OrderSummaryText,
  LineItems,
  AllLineItemsAndFees,
  ServiceFeesToggle,
  ServiceFeeItems,
  Total,
  Divider,
  TicketProtectionFee,
  DueNow,
  DueLater,
  EventImage,
  EventInfo,

  // TODO: components need completing
  PromoCodeInput,
  PromoCodeSummary,
};
